import { Box, Button, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import useAssetRetrieval, { AssetRetrieval } from "hooks/assetRetrievals/useAssetRetrieval";
import { OrganizationConfigurationContext } from "hooks/useOrganizationConfiguration";
import useEarnings from "hooks/earnings/useEarnings";
import theme from "lib/theme";
import { useContext, useMemo, useState } from "react";
import { ServiceTermsModal } from "./ServiceTermsModal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OrderSummaryLoadingComponent from "./OrderSummaryLoadingComponent";

interface AssetCartProps {
  onContinue: () => void;
  continueLabel?: string;
  disableContinue?: boolean;
}

function AssetCart({ onContinue, continueLabel = "Continue", disableContinue }: AssetCartProps) {
  const { values } = useFormikContext<AssetRetrieval>();
  const { pricing, loading: isPricingLoading } = useAssetRetrieval({
    quantity: 1,
    insuranceTotalInCents: (Number(values.insuranceOption) || 0) * 100,
    virtualPickup: values.virtualPickup || false,
    boxType: values.boxType ?? undefined,
    country: values.employeeAddressCountry ?? "United States",
  });
  const { earningsBalance, isLoading: isEarningsLoading } = useEarnings();
  const total = pricing?.totalInCents ? Number(pricing.totalInCents / 100) : 0;
  const basePrice = pricing?.basePriceInCents ? Number(pricing.basePriceInCents / 100) : 0;
  const taxPrice = pricing?.taxInCents ? Number(pricing.taxInCents / 100) : 0;
  const discountAmount = pricing?.discountInCents ? Number(pricing.discountInCents / 100) : 0;
  const processingFee = pricing?.miscellaneousFeesInCents ? Number(pricing.miscellaneousFeesInCents / 100) : 0;
  const dataWipeFee = pricing?.serviceFeeInCents ? Number(pricing.serviceFeeInCents / 100) : 0;
  const insufficientFunds = total > earningsBalance;
  const { assetRetrievalTermsSigned } = useContext(OrganizationConfigurationContext);
  const [openTerms, setOpenTerms] = useState(false);

  const shouldOpenTerms = useMemo(() => {
    return continueLabel === "place order" && !assetRetrievalTermsSigned;
  }, [continueLabel, assetRetrievalTermsSigned]);

  if (isPricingLoading || isEarningsLoading) {
    return <OrderSummaryLoadingComponent continueLabel={continueLabel} />;
  }

  return (
    <div style={{ position: "sticky", top: "95px" }}>
      <Box
        sx={{
          py: 3,
          px: 4,
          background: "white",
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
        }}
      >
        <Typography fontWeight="bold" variant="h6">
          Order Summary
        </Typography>

        <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
            <Box>Device Box & Shipping Flat Fee</Box>
            <Box>${basePrice}</Box>
          </Box>
          {Number(values.insuranceOption) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
              <Box>Insurance</Box>
              <Box>${values.insuranceOption}</Box>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
            <Box>Processing Fees</Box>
            <Box>${processingFee}</Box>
          </Box>
          {values.virtualPickup ? (
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
              <Box>Data Wipe & Audit Fee</Box>
              <Box>${dataWipeFee}</Box>
            </Box>
          ) : null}
          {pricing?.taxPercentage && pricing.taxPercentage > 0 ? (
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
              <Box>
                Estimated tax ({pricing?.taxPercentage}%)
                <Tooltip title="This tax is based on state sales tax rates for your organization's location">
                  <IconButton size="small" sx={{ pl: 1, color: "text.secondary" }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>${taxPrice}</Box>
            </Box>
          ) : null}
          {!values.virtualPickup && pricing?.discountPercentage && pricing.discountPercentage > 0 ? (
            <Box sx={{ display: "flex", justifyContent: "space-between", color: theme.palette.primaryPro.main }}>
              <Box>Beta Discount ({pricing?.discountPercentage}%)</Box>
              <Box>-${discountAmount}</Box>
            </Box>
          ) : (
            ""
          )}
          {pricing?.waivedFees && (
            <Box sx={{ display: "flex", justifyContent: "space-between", color: theme.palette.primaryPro.main }}>
              <Box>Waived Fees</Box>
              <Box>-${total}</Box>
            </Box>
          )}
        </Box>
        <Divider />
        <Box mt={2} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography fontSize={"20px"} fontWeight="bold" color="primary">
            Total
          </Typography>
          <Typography fontSize={"20px"} fontWeight="bold" color="primary">
            ${pricing?.waivedFees ? 0 : total}
          </Typography>
        </Box>
        <Box mt={1} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
            New Earnings Balance: $
            {Number(insufficientFunds ? 0 : earningsBalance - (pricing?.waivedFees ? 0 : total)).toFixed(2)}
          </Box>
        </Box>
        <Box mt={5}>
          <span>
            <Button
              fullWidth
              variant="contained"
              onClick={shouldOpenTerms ? () => setOpenTerms(true) : onContinue}
              disabled={disableContinue}
            >
              {continueLabel}
            </Button>
          </span>
        </Box>
        <ServiceTermsModal open={openTerms} setOpen={setOpenTerms} submit={onContinue} />
      </Box>
    </div>
  );
}

export default AssetCart;
